/*
 * ReturnToOriginBar — "Back to …" above a form that was opened from another page
 * (My Jobs ▸ Resume). Most target forms open on a list, which has no Close of its
 * own, so this is the one way back that every form shares. Renders nothing for a
 * form opened the normal way.
 */
import { useSyncExternalStore } from 'react';
import { useHotkeys, formatCombo } from './hotkeys';
import { getReturnOrigin, returnToOrigin, subscribeReturnOrigin } from '../layouts/navigateTo';
import './ReturnToOriginBar.css';

const BACK_COMBO = 'alt+m';

export function ReturnToOriginBar() {
  const origin = useSyncExternalStore(subscribeReturnOrigin, getReturnOrigin);
  useHotkeys(origin ? { combo: BACK_COMBO, label: `Back to ${origin.label}`, group: 'Global', run: () => { returnToOrigin(); } } : null);
  if (!origin) return null;
  return (
    <ReturnBar label={origin.label} detail={origin.detail} onBack={() => { returnToOrigin(); }}
      title={`Back to ${origin.label} (${formatCombo(BACK_COMBO)})`} />
  );
}

/**
 * The same "Back to …" band, for a screen that opens another one IN PLACE rather than through
 * navigateToPage — e.g. PLM Result Entry's Run Reference QC, which shows the Daily QC read inside
 * the batch (owner 2026-11-03: "no option back to PLM Result Entry"). The caller owns the way back.
 * No hotkey of its own: the screen under it keeps its keys (Daily QC binds Alt+M to Comment).
 */
export function ReturnBar({ label, detail, onBack, title, inline }: {
  label: string; detail?: string; onBack: () => void; title?: string; inline?: boolean;
}) {
  return (
    <div className={`return-bar${inline ? ' return-bar--inline' : ''}`} role="navigation" aria-label={`Back to ${label}`}>
      <button type="button" className="ca-btn return-bar-back" title={title ?? `Back to ${label}`} onClick={onBack}>
        <i className="ti ti-arrow-left" aria-hidden="true" /> Back to {label}
      </button>
      {detail && (
        <span className="return-bar-detail">
          <i className="ti ti-briefcase" aria-hidden="true" />{detail}
        </span>
      )}
    </div>
  );
}
